import { useState, FormEvent } from 'react';

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d7bqqcqqofe8vli40npg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Info */}
          <div>
            <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Get In Touch</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-3 mb-6 leading-tight">
              Start Your Drainage<br />Protection Today
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-10">
              Ready to protect your property? Fill in the form and one of our friendly team will be in touch within 2 hours to discuss the best plan for you.
            </p>

            {/* Contact Details */}
            <div className="flex flex-col gap-5 mb-10">
              {[
                { icon: 'ri-phone-line', label: 'Call Us', value: '08000487055', sub: 'Mon–Fri 8am–6pm, Sat 9am–4pm' },
                { icon: 'ri-mail-line', label: 'Email Us', value: 'info@elitedrain-services.com', sub: 'We reply within 2 hours' },
                { icon: 'ri-map-pin-line', label: 'Service Area', value: 'South & South West England', sub: 'Bristol, Bournemouth, Southampton & more' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-orange-500 text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wide">{item.label}</div>
                    <div className="text-gray-900 font-semibold text-sm mt-0.5">{item.value}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Emergency Banner */}
            <div className="bg-gray-900 rounded-2xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-xl flex-shrink-0">
                <i className="ri-alarm-warning-line text-orange-400 text-2xl"></i>
              </div>
              <div>
                <div className="text-white font-bold text-sm">Drainage Emergency?</div>
                <div className="text-gray-400 text-xs mt-1">Call our 24/7 emergency line for immediate assistance.</div>
                <div className="text-orange-400 font-bold text-lg mt-1">08000487055</div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-green-500 text-3xl"></i>
                </div>
                <h3 className="text-gray-900 font-bold text-xl">Message Sent!</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Thanks for getting in touch. One of our team will contact you within 2 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-4 text-orange-500 font-semibold text-sm cursor-pointer hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-gray-900 font-bold text-xl mb-6">Request a Callback</h3>
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="John"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Smith"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="07700 900000"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                      Interested In
                    </label>
                    <select
                      name="plan_interest"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer"
                    >
                      <option value="">Select a plan...</option>
                      <option value="monthly_plan">Monthly Plan (£150 setup + £7.99/mo)</option>
                      <option value="allinone_plan">All-In-One Monthly (£29.99/mo)</option>
                      <option value="commercial_plan">Commercial Plan</option>
                      <option value="not_sure">Not sure yet</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-gray-600 text-xs font-semibold uppercase tracking-wide block mb-1.5">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your property and any drainage concerns..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                    ></textarea>
                    <div className="text-right text-xs text-gray-400 mt-1">{charCount}/500</div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold text-sm py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap mt-2"
                  >
                    {submitting ? 'Sending...' : 'Request a Callback'}
                  </button>

                  <p className="text-gray-400 text-xs text-center">
                    By submitting this form you agree to our privacy policy. We never share your data.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
